import React, { useState, useCallback, useRef, useEffect } from 'react'
import type {
  SongSettings,
  SectionStructure,
  StoryBox,
  AIFeedbackPreferences,
  AISettings
} from '../lib/api'
import { createDefaultSectionStructure } from '../lib/api'
import { GENRE_OPTIONS, VERBOSITY_OPTIONS } from '../types/song'
import { createDefaultAIFeedbackPreferences, createDefaultAISettings } from '../utils/songDefaults'
import { Cog6ToothIcon, XMarkIcon } from '@heroicons/react/24/outline'
// import SettingsAutoDetection from './SettingsAutoDetection'  // Disabled for Phase 5
import { FoundationTab } from './settings/tabs/FoundationTab'
import { BoxesTab } from './settings/tabs/BoxesTab'
import { createDefaultBoxes } from './settings/tabs/BoxesTab/constants'
import { StructureTab } from './settings/tabs/StructureTab'
import { SoundTab } from './settings/tabs/SoundTab'
import { StyleTab } from './settings/tabs/StyleTab'
import { TabNavigation } from './settings/shared/TabNavigation'
import type { TabConfig } from './settings/shared/TabNavigation'
import { HelpSidebar } from './settings/HelpSidebar'
import { GuidedTour } from './settings/GuidedTour'
import { useTourProgress } from './settings/GuidedTour/useTourProgress'
import { useKeyboardShortcuts } from './settings/hooks/useKeyboardShortcuts'
import { useAutoSave } from './settings/hooks/useAutoSave'
import { Toast } from './settings/shared/Toast'
import type { ToastType } from './settings/shared/Toast'
import './settings/styles/settingsTheme.css'

interface SongSettingsPanelProps {
  settings: SongSettings
  onSettingsChange: (settings: SongSettings) => void
  isOpen: boolean
  onClose: () => void
}

// 6-tab configuration for consolidated settings modal
const SETTINGS_TABS: TabConfig[] = [
  { id: 'foundation', label: 'Foundation', icon: '🏛️' },
  { id: 'boxes', label: 'Boxes', icon: '📦' },
  { id: 'structure', label: 'Structure', icon: '🏗️' },
  { id: 'sound', label: 'Sound', icon: '🎵' },
  { id: 'style', label: 'Style', icon: '🎨' },
  { id: 'ai', label: 'AI', icon: '🤖' }
]

export const SongSettingsPanel: React.FC<SongSettingsPanelProps> = ({
  settings,
  onSettingsChange,
  isOpen,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<string>('foundation')
  // const [showAutoDetection, setShowAutoDetection] = useState(false)  // Disabled for Phase 5
  // FIX #1: Initialize sidebar visibility based on viewport width
  const [isSidebarVisible, setIsSidebarVisible] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1280 : true
  )
  const [isTourActive, setIsTourActive] = useState(false)
  const [toast, setToast] = useState<{ type: ToastType; message: string } | null>(null)

  // FIX #2: Use tour progress hook - markTourCompleted syncs state when tour finishes
  const { hasCompletedTour, markTourCompleted, resetTour } = useTourProgress()

  // Use ref to avoid stale closure issues with settings
  const settingsRef = useRef<SongSettings>(settings)

  useEffect(() => {
    settingsRef.current = settings
  }, [settings])

  // Stable default boxes - created once and reused until real boxes arrive
  const defaultBoxesRef = useRef<StoryBox[] | null>(null)

  // Compute effective boxes - if none exist, use defaults (FR-3: 3 boxes by default)
  // This ensures boxes are always available without relying on async effects
  const effectiveBoxes = React.useMemo(() => {
    if (settings.boxes && settings.boxes.length > 0) {
      // Real boxes exist, use them and clear the default ref
      defaultBoxesRef.current = null
      return settings.boxes
    }
    // No real boxes - use stable defaults
    if (!defaultBoxesRef.current) {
      defaultBoxesRef.current = createDefaultBoxes()
    }
    return defaultBoxesRef.current
  }, [settings.boxes])

  // NOTE: We do NOT auto-persist default boxes on load. This prevents the
  // "song saves immediately on load" regression. Default boxes are shown in the UI
  // via effectiveBoxes, but only saved when the user actually makes a change
  // (edits content, adds/removes boxes). The first user action will include
  // the default boxes in the save payload.

  // Handle settings changes with proper typing
  // CRITICAL: Update settingsRef immediately (optimistic) so sequential edits merge correctly
  const updateSettings = useCallback(<K extends keyof SongSettings>(
    key: K,
    value: SongSettings[K]
  ) => {
    const newSettings = {
      ...settingsRef.current,
      [key]: value
    }
    // Update ref immediately for optimistic UI and correct sequential merging
    settingsRef.current = newSettings
    onSettingsChange(newSettings)
  }, [onSettingsChange])

  // Handle nested object updates
  // This uses updateSettings which now handles settingsRef sync automatically
  const updateNestedSettings = useCallback(<T extends Record<string, unknown>>(
    parentKey: keyof SongSettings,
    childKey: keyof T,
    value: unknown
  ) => {
    const parentValue = settingsRef.current[parentKey] as T
    const updatedValue: SongSettings[typeof parentKey] = {
      ...parentValue,
      [childKey]: value
    }
    updateSettings(parentKey, updatedValue)
  }, [updateSettings])


  // Add new section structure
  const addSectionStructure = useCallback(() => {
    const newOrder = settingsRef.current.section_structure.length
    const newSection = createDefaultSectionStructure(newOrder)
    updateSettings('section_structure', [...settingsRef.current.section_structure, newSection])
  }, [updateSettings])

  // Remove section structure
  const removeSectionStructure = useCallback((index: number) => {
    const newSections = settingsRef.current.section_structure.filter((_, i) => i !== index)
    // Reorder remaining sections
    const reorderedSections = newSections.map((section, i) => ({ ...section, order: i }))
    updateSettings('section_structure', reorderedSections)
  }, [updateSettings])

  // Update section structure
  const updateSectionStructure = useCallback(<K extends keyof SectionStructure>(
    index: number,
    key: K,
    value: SectionStructure[K]
  ) => {
    const newSections = [...settingsRef.current.section_structure]
    newSections[index] = { ...newSections[index], [key]: value }
    updateSettings('section_structure', newSections)
  }, [updateSettings])

  // Handle array updates (tags, keywords, etc.)
  const updateArrayField = useCallback((
    parentKey: keyof SongSettings,
    childKey: string,
    value: string[]
  ) => {
    updateNestedSettings(parentKey, childKey, value)
  }, [updateNestedSettings])

  // Memoized handler for FoundationTab settings changes
  // CRITICAL: Must be memoized to avoid stale closures in debounced save callbacks
  // Without this, debounced saves can capture old function references and fail silently
  const handleFoundationSettingsChange = useCallback((path: string, value: unknown) => {
    // Parse path like "foundation_questions.who_is_talking" or "narrative_pov"
    const parts = path.split('.')
    if (parts.length === 1) {
      // Top-level key like narrative_pov
      updateSettings(parts[0] as keyof SongSettings, value as SongSettings[keyof SongSettings])
    } else if (parts.length === 2) {
      // Nested key like foundation_questions.who_is_talking or six_best_friends.who
      updateNestedSettings(parts[0] as keyof SongSettings, parts[1], value)
    }
  }, [updateSettings, updateNestedSettings])

  // Handler for BoxesTab changes
  const handleBoxesChange = useCallback((boxes: StoryBox[]) => {
    updateSettings('boxes', boxes)
  }, [updateSettings])

  // Add/remove from arrays
  const addToArray = useCallback((
    parentKey: keyof SongSettings,
    childKey: string,
    value: string
  ) => {
    const current = (settings[parentKey] as Record<string, string[]>)[childKey]
    if (!current.includes(value)) {
      updateArrayField(parentKey, childKey, [...current, value])
    }
  }, [settings, updateArrayField])

  const removeFromArray = useCallback((
    parentKey: keyof SongSettings,
    childKey: string,
    value: string
  ) => {
    const current = (settings[parentKey] as Record<string, string[]>)[childKey]
    updateArrayField(parentKey, childKey, current.filter(item => item !== value))
  }, [settings, updateArrayField])

  // Handler for AI settings updates (nested under settings.ai)
  const updateAISetting = useCallback(<K extends keyof AISettings>(
    key: K,
    value: AISettings[K]
  ) => {
    const currentAI = settingsRef.current.ai || createDefaultAISettings()
    const updatedAI: AISettings = {
      ...currentAI,
      [key]: value
    }
    updateSettings('ai', updatedAI)
  }, [updateSettings])

  // Handler for AI feedback preferences updates (nested under settings.ai.ai_feedback_preferences)
  const updateAIFeedbackPreference = useCallback(<K extends keyof AIFeedbackPreferences>(
    key: K,
    value: AIFeedbackPreferences[K]
  ) => {
    // Guard against undefined preferences (for older songs)
    const currentAI = settingsRef.current.ai || createDefaultAISettings()
    const currentPrefs = currentAI.ai_feedback_preferences || createDefaultAIFeedbackPreferences()
    const updatedPrefs: AIFeedbackPreferences = {
      ...currentPrefs,
      [key]: value
    }
    const updatedAI: AISettings = {
      ...currentAI,
      ai_feedback_preferences: updatedPrefs
    }
    updateSettings('ai', updatedAI)
  }, [updateSettings])

  // Handler for nested AI feedback preferences (enabled_features)
  const updateAIFeedbackFeature = useCallback((
    feature: keyof AIFeedbackPreferences['enabled_features'],
    enabled: boolean
  ) => {
    const currentAI = settingsRef.current.ai || createDefaultAISettings()
    const currentPrefs = currentAI.ai_feedback_preferences || createDefaultAIFeedbackPreferences()
    const updatedPrefs: AIFeedbackPreferences = {
      ...currentPrefs,
      enabled_features: {
        ...currentPrefs.enabled_features,
        [feature]: enabled
      }
    }
    const updatedAI: AISettings = {
      ...currentAI,
      ai_feedback_preferences: updatedPrefs
    }
    updateSettings('ai', updatedAI)
  }, [updateSettings])

  // Auto-save hook (simulated - settings are saved immediately on change)
  const { saveStatus: autoSaveStatus, triggerSave, retryFailedSave } = useAutoSave({
    onSave: async () => {
      // Settings are already saved via onSettingsChange
      // This hook tracks the save status for UI feedback
      await new Promise(resolve => setTimeout(resolve, 500))
    },
    debounceMs: 1000
  })

  // Update save status when settings change
  useEffect(() => {
    if (isOpen) {
      triggerSave()
    }
  }, [settings, isOpen, triggerSave])

  // FIX #1: Sync sidebar visibility on window resize
  useEffect(() => {
    const handleResize = () => {
      // Auto-hide sidebar when resizing from desktop to mobile
      if (window.innerWidth < 1280 && isSidebarVisible) {
        setIsSidebarVisible(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isSidebarVisible])

  // Track previous save status for success toast after recovery
  const prevAutoSaveStatusRef = useRef(autoSaveStatus)

  // FIX #3: Show toast notification on auto-save error or success after retry
  useEffect(() => {
    const prevStatus = prevAutoSaveStatusRef.current
    prevAutoSaveStatusRef.current = autoSaveStatus

    if (autoSaveStatus === 'error') {
      setToast({ type: 'error', message: 'Failed to save settings. Click retry to try again.' })
    } else if (autoSaveStatus === 'idle' && prevStatus === 'error') {
      // Success after error recovery - show success toast
      setToast({ type: 'success', message: 'Settings saved successfully.' })
    }
  }, [autoSaveStatus])

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSave: () => {
      // Settings auto-save, but we can trigger an explicit save if needed
      triggerSave()
    },
    onToggleHelp: () => {
      setIsSidebarVisible(prev => !prev)
    },
    onStartTour: () => {
      setIsTourActive(true)
    },
    onClose: onClose,
    isModalOpen: isOpen
  })

  if (!isOpen) return null

  return (
    <>
      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        {/* Modal Content - Light Theme with Sidebar Layout */}
        <div
          className="settings-modal-light rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] overflow-hidden flex"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Main Content Area */}
          <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="settings-header flex items-center justify-between p-6 border-b flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="settings-header-icon p-2 rounded-lg border">
                <Cog6ToothIcon className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="settings-header-title font-bold text-xl">Song Settings</h2>
            </div>
            <button
              data-testid="settings-modal-close"
              onClick={onClose}
              className="settings-close-button p-2 rounded-lg transition-colors"
              title="Close Settings"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Tab Navigation - Using TabNavigation component */}
          <TabNavigation
            tabs={SETTINGS_TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Content Area - All panels stay mounted, visibility toggled via hidden/aria-hidden */}
          <div className="settings-content flex-1 overflow-y-auto p-6 space-y-6 min-h-0" data-testid="settings-content-area">
            {/* Foundation Tab Panel */}
            <div
              id="foundation-panel"
              role="tabpanel"
              aria-labelledby="foundation-tab"
              hidden={activeTab !== 'foundation'}
              aria-hidden={activeTab !== 'foundation'}
              data-testid="foundation-tab-content"
              className={activeTab === 'foundation' ? 'settings-tab-fade-in' : ''}
            >
              <FoundationTab
                settings={settings}
                onSettingsChange={handleFoundationSettingsChange}
              />
            </div>

            {/* Boxes Tab Panel */}
            <div
              id="boxes-panel"
              role="tabpanel"
              aria-labelledby="boxes-tab"
              hidden={activeTab !== 'boxes'}
              aria-hidden={activeTab !== 'boxes'}
              data-testid="boxes-tab-content"
              className={activeTab === 'boxes' ? 'settings-tab-fade-in' : ''}
            >
              <BoxesTab
                boxes={effectiveBoxes}
                onChange={handleBoxesChange}
              />
            </div>

            {/* Structure Tab Panel */}
            <div
              id="structure-panel"
              role="tabpanel"
              aria-labelledby="structure-tab"
              hidden={activeTab !== 'structure'}
              aria-hidden={activeTab !== 'structure'}
              data-testid="structure-tab-content"
              className={activeTab === 'structure' ? 'settings-tab-fade-in' : ''}
            >
              <StructureTab
                settings={settings}
                onUpdateSettings={updateSettings}
                onUpdateSectionStructure={updateSectionStructure}
                onAddSectionStructure={addSectionStructure}
                onRemoveSectionStructure={removeSectionStructure}
              />
            </div>

            {/* Sound Tab Panel */}
            <div
              id="sound-panel"
              role="tabpanel"
              aria-labelledby="sound-tab"
              hidden={activeTab !== 'sound'}
              aria-hidden={activeTab !== 'sound'}
              data-testid="sound-tab-content"
              className={activeTab === 'sound' ? 'settings-tab-fade-in' : ''}
            >
              <SoundTab
                settings={settings}
                onUpdateNestedSettings={updateNestedSettings}
                onAddToArray={addToArray}
                onRemoveFromArray={removeFromArray}
              />
            </div>

            {/* Style Tab Panel */}
            <div
              id="style-panel"
              role="tabpanel"
              aria-labelledby="style-tab"
              hidden={activeTab !== 'style'}
              aria-hidden={activeTab !== 'style'}
              data-testid="style-tab-content"
              className={activeTab === 'style' ? 'settings-tab-fade-in' : ''}
            >
              <StyleTab
                settings={settings}
                onUpdateSettings={updateSettings}
              />
            </div>

            {/* AI Tab Panel */}
            <div
              id="ai-panel"
              role="tabpanel"
              aria-labelledby="ai-tab"
              hidden={activeTab !== 'ai'}
              aria-hidden={activeTab !== 'ai'}
              className={`space-y-8 ${activeTab === 'ai' ? 'settings-tab-fade-in' : ''}`}
              data-testid="ai-tab-content"
            >
              {/* AI Assistance Section */}
              {(() => {
                // Guard against undefined AI settings (older songs)
                const aiSettings = settings.ai || createDefaultAISettings()
                return (
              <div className="space-y-6">
                <h3 className="text-heading font-medium text-gray-900">AI Assistance</h3>

                {/* AI Creativity Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AI Creativity Level: {aiSettings.creativity_level}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={aiSettings.creativity_level}
                    onChange={(e) => updateAISetting('creativity_level', parseInt(e.target.value))}
                    className="w-full"
                    data-testid="ai-creativity-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Conservative</span>
                    <span>Experimental</span>
                  </div>
                </div>

                {/* AI Options */}
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={aiSettings.preserve_user_phrases}
                      onChange={(e) => updateAISetting('preserve_user_phrases', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 mr-2 focus:ring-blue-500"
                      data-testid="preserve-phrases-toggle"
                    />
                    <span className="text-sm text-gray-700">Preserve user's original phrases when editing</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={aiSettings.auto_suggestions}
                      onChange={(e) => updateAISetting('auto_suggestions', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 mr-2 focus:ring-blue-500"
                      data-testid="auto-suggestions-toggle"
                    />
                    <span className="text-sm text-gray-700">Enable automatic suggestions</span>
                  </label>
                </div>
              </div>
                )
              })()}

              {/* AI Feedback Preferences Section */}
              <div className="space-y-6 pt-6 border-t border-gray-200">
                <div>
                  <h3 className="text-heading font-medium text-gray-900">AI Feedback Preferences</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    These settings customize how AI provides feedback. They do not affect the ambient rhyme stability display.
                  </p>
                </div>

                {(() => {
                  // Guard against undefined preferences (older songs)
                  const aiSettings = settings.ai || createDefaultAISettings()
                  const prefs = aiSettings.ai_feedback_preferences || createDefaultAIFeedbackPreferences()

                  return (
                    <>
                      {/* Rhyme Stability Preference */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Rhyme Stability Preference: {prefs.stability_preference}
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={prefs.stability_preference}
                          onChange={(e) => updateAIFeedbackPreference('stability_preference', parseInt(e.target.value))}
                          className="w-full"
                          data-testid="ai-stability-slider"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Experimental</span>
                          <span>Stable</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          This setting affects AI feedback only, not the ambient stability display in the Sound tab.
                        </p>
                      </div>

                      {/* Genre Context */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Genre Context
                        </label>
                        <select
                          value={prefs.genre_context || ''}
                          onChange={(e) => {
                            const newGenre = e.target.value || null
                            updateAIFeedbackPreference('genre_context', newGenre)
                            // Clear custom_genre when switching away from custom
                            if (newGenre !== 'custom' && prefs.custom_genre) {
                              updateAIFeedbackPreference('custom_genre', null)
                            }
                          }}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          data-testid="ai-genre-selector"
                        >
                          <option value="">No specific genre</option>
                          {GENRE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Help AI tailor feedback to your genre's conventions.
                        </p>

                        {/* Custom Genre Input */}
                        {prefs.genre_context === 'custom' && (
                          <input
                            type="text"
                            value={prefs.custom_genre || ''}
                            onChange={(e) => updateAIFeedbackPreference('custom_genre', e.target.value)}
                            placeholder="Enter custom genre..."
                            className="mt-2 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            data-testid="ai-custom-genre-input"
                          />
                        )}
                      </div>

                      {/* Feedback Verbosity */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Feedback Verbosity
                        </label>
                        <select
                          value={prefs.feedback_verbosity}
                          onChange={(e) => updateAIFeedbackPreference('feedback_verbosity', e.target.value as 'low' | 'medium' | 'high')}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          data-testid="ai-verbosity-selector"
                        >
                          {VERBOSITY_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Control how detailed AI feedback should be.
                        </p>
                      </div>

                      {/* Feature Toggles */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enabled Feedback Types
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Choose which types of AI feedback you want to receive.
                        </p>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={prefs.enabled_features.rhyme}
                              onChange={(e) => updateAIFeedbackFeature('rhyme', e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 mr-2 focus:ring-blue-500"
                              data-testid="ai-feature-rhyme-toggle"
                            />
                            <span className="text-sm text-gray-700">Rhyme feedback</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={prefs.enabled_features.prosody}
                              onChange={(e) => updateAIFeedbackFeature('prosody', e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 mr-2 focus:ring-blue-500"
                              data-testid="ai-feature-prosody-toggle"
                            />
                            <span className="text-sm text-gray-700">Prosody feedback</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={prefs.enabled_features.structure}
                              onChange={(e) => updateAIFeedbackFeature('structure', e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 mr-2 focus:ring-blue-500"
                              data-testid="ai-feature-structure-toggle"
                            />
                            <span className="text-sm text-gray-700">Structure feedback</span>
                          </label>
                        </div>
                      </div>
                    </>
                  )
                })()}
              </div>
            </div>
          </div>
          </div>

          {/* Help Sidebar */}
          <HelpSidebar
            activeTab={activeTab}
            isVisible={isSidebarVisible}
            onToggle={() => setIsSidebarVisible(!isSidebarVisible)}
            saveStatus={autoSaveStatus}
            onRetry={retryFailedSave}
            onStartTour={() => setIsTourActive(true)}
            hasCompletedTour={hasCompletedTour}
            onResetTour={resetTour}
          />
        </div>
      </div>

      {/* Guided Tour */}
      <GuidedTour
        isActive={isTourActive}
        onComplete={() => setIsTourActive(false)}
        onTabChange={setActiveTab}
        onTourComplete={markTourCompleted}
      />

      {/* Toast Notifications */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {/* Auto-Detection Modal - Disabled for Phase 5 (requires lyrics prop) */}
      {/* TODO: Re-enable when lyrics prop is added to SongSettingsPanel */}
      {/* {showAutoDetection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4">
          <SettingsAutoDetection
            lyrics={lyrics}
            currentSettings={settings}
            onApplySettings={(newSettings) => {
              onSettingsChange({
                ...settings,
                ...newSettings
              })
              setShowAutoDetection(false)
            }}
            onClose={() => setShowAutoDetection(false)}
          />
        </div>
      )} */}
    </>
  )
}

export default SongSettingsPanel
